<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  Mutation,
  PermissionOrganization,
  UpdateStep,
  UpdateStepDataQuery,
  UpdateStepDataQueryVariables,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import { computed, reactive, ref, watch } from 'vue'
import ToolPicker from '@/components/input/ToolPicker.vue'
import StepTypePicker from '@/components/input/StepTypePicker.vue'
import StepWarningField from '@/components/step/StepWarningField.vue'
import StepIconsField from '@/components/step/StepIconsField.vue'
import TextAreaField from '@/components/input/TextAreaField.vue'
import TextField from '@/components/input/TextField.vue'
import useVuelidate from '@vuelidate/core'
import { minValue, normalName, required } from '@/validation'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import StepToolbar from '@/components/step/StepToolbar.vue'
import ArticleAllocationList from '@/components/article/ArticleAllocationList.vue'
import StepCheckConfigurations from '@/components/checkConfiguration/StepCheckConfigurations.vue'
import { associatedIssueTableIssueLinkFragment } from '@/components/issue/issueFragments'
import AssociatedIssuesTable from '@/components/issue/AssociatedIssuesTable.vue'
import { mockUpdateStep } from '@/generated/graphql-mocks'
import DurationField from '@/components/input/DurationField.vue'
import StepUploadList from '@/components/stepUpload/StepUploadList.vue'
import StepOutputsSection from '@/components/step/StepOutputsSection.vue'
import StepInputsSection from '@/components/step/StepInputsSection.vue'
import useAuthorization from '@/composables/useAuthorization'

type Model = Partial<UpdateStep>
const modelKeys = Object.keys(mockUpdateStep())

const props = defineProps<{
  siteId: string
  stepId: string
  moduleId: string
  bopId: string
  hasGlbFile?: boolean
  isSection?: boolean
}>()
const emit = defineEmits<{
  updated: [id: string]
  deleted: []
  preview: []
  promote: []
}>()

const { t } = useI18n()
const { reactiveOrgAuthz, hasOrgAuthz } = useAuthorization()

type LocalStep = UpdateStepDataQuery['product']['step']
const fetchQuery = useQuery<UpdateStepDataQuery, UpdateStepDataQueryVariables>(
  gql`
    query UpdateStepData($id: ID!, $includeIssues: Boolean!) {
      product {
        step(id: $id) {
          id
          durationInSeconds
          instructionText
          warningText
          name
          stepType {
            id
            name
          }
          toolIds
          tools {
            id
            name
            imageUploads {
              id
              imageUrl(options: { height: 500, width: 500 })
            }
          }
          issueLinks @include(if: $includeIssues) {
            ...AssociatedIssueTableIssueLink
          }
        }
      }
    }

    ${associatedIssueTableIssueLinkFragment}
  `,
  () => ({
    id: props.stepId as string,
    includeIssues: reactiveOrgAuthz(PermissionOrganization.OrgIssueRead).value,
  }),
  () => ({
    enabled: !!props.stepId,
  }),
)
const step = computed(() => fetchQuery.result.value?.product.step)
const issueLinks = computed(() => step.value?.issueLinks || [])

const model = reactive<Model>({})
const validation = useVuelidate<Model>(
  {
    name: { required, normalName },
    stepTypeId: { required },
    durationInSeconds: { min: minValue(0) },
  },
  model,
)

watch(step, (v) => {
  if (!v) {
    return
  }

  modelKeys.forEach((k) => {
    ;(model as Record<string, unknown>)[k as keyof Model] = v[k as keyof LocalStep]
  })
  model.stepTypeId = v.stepType.id
  validation.value.$reset()
})

const notifySaved = ref(false)
const updateMutation = useMutation<Mutation, { step: UpdateStep }>(gql`
  mutation updateStep($step: UpdateStep!) {
    product {
      updateStep(command: $step) {
        id
      }
    }
  }
`)

function update() {
  updateMutation.mutate({ step: model as UpdateStep }).then(() => {
    notifySaved.value = true
    emit('updated', props.stepId)
  })
}

// Tools persist immediately on change (no dedicated save button in the tools card).
function saveTools(ids: string[] | undefined) {
  model.toolIds = ids ?? []
  update()
}

// The model still holds the previous step until its query resolves, so only expose a matching one.
const draft = computed(() => (model.id === props.stepId ? model : undefined))
defineExpose({ draft })
</script>

<template>
  <h2 style="position: relative">
    {{ model.name }}
    <step-toolbar :step-id="props.stepId" @deleted="emit('deleted')">
      <v-btn
        v-if="!props.isSection"
        icon="visibility"
        density="compact"
        variant="flat"
        :title="t('component.stepPreviewDialog.button')"
        @click="emit('preview')"
      />
      <v-btn
        icon="arrow_upward"
        density="compact"
        variant="flat"
        :title="t('component.promoteStepToModule.button')"
        @click="emit('promote')"
      />
    </step-toolbar>
  </h2>

  <p v-if="props.isSection" class="text-medium-emphasis text-body-2 mt-4">
    {{ t('component.updateStep.sectionHint') }}
  </p>

  <div :class="{ 'section-unused': props.isSection }" :inert="props.isSection">
    <h3 class="first-heading">{{ t('entity.stepInput.plural') }}</h3>
    <step-inputs-section
      :bop-id="bopId"
      :module-id="moduleId"
      :step-id="stepId"
      :show-colors="props.hasGlbFile"
      @updated="emit('updated', props.stepId)"
    />

    <h3>{{ t('entity.article.plural') }}</h3>
    <article-allocation-list
      label="Parts"
      :bop-id="props.bopId"
      :step-id="props.stepId"
      :show-colors="props.hasGlbFile"
      @updated="emit('updated', props.stepId)"
    />

    <h3>{{ t('entity.stepOutput.plural') }}</h3>
    <step-outputs-section :module-id="moduleId" :step-id="stepId" :bop-id="bopId" />
  </div>

  <h3>{{ t('entity.step.singular') }}</h3>
  <v-card>
    <v-card-text>
      <text-field
        v-model="model.name"
        :label="t('entity.step.field.name')"
        :validation="validation.name"
        required
      />
      <step-type-picker
        v-model="model.stepTypeId"
        :label="t('entity.step.field.stepType')"
        :validation="validation.stepTypeId"
        required
      />
      <duration-field
        v-model="model.durationInSeconds"
        :label="t('entity.step.field.durationInSeconds')"
        :validation="validation.durationInSeconds"
        :disabled="props.isSection"
      />
      <text-area-field
        v-model="model.instructionText"
        :label="t('entity.step.field.instructionText')"
        :validation="validation.instructionText"
        :disabled="props.isSection"
        markdown
      />
      <step-warning-field v-model="model.warningText" :disabled="props.isSection" />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        color="primary"
        variant="elevated"
        :loading="updateMutation.loading.value"
        @click="update()"
      >
        {{ t('button.save') }}
      </v-btn>
    </v-card-actions>
  </v-card>

  <div :class="{ 'section-unused': props.isSection }" :inert="props.isSection">
    <h3>{{ t('entity.step.field.tools') }}</h3>
    <v-card>
      <v-card-text>
        <tool-picker
          :model-value="model.toolIds"
          :site-id="props.siteId"
          :disabled="props.isSection"
          @update:model-value="saveTools"
        />
      </v-card-text>
    </v-card>

    <h3>{{ t('entity.step.field.icons') }}</h3>
    <v-card>
      <v-card-text>
        <step-icons-field
          :step-id="props.stepId"
          :disabled="props.isSection"
          @updated="emit('updated', props.stepId)"
        />
      </v-card-text>
    </v-card>

    <h3>{{ t('entity.stepUpload.plural') }}</h3>
    <step-upload-list :step-id="stepId" />

    <h3>{{ t('entity.checkConfiguration.plural') }}</h3>
    <step-check-configurations :bop-id="bopId" :module-id="moduleId" :step-id="stepId" />

    <template v-if="hasOrgAuthz(PermissionOrganization.OrgIssueRead)">
      <h3>{{ t('entity.issue.plural') }}</h3>
      <associated-issues-table :links="issueLinks" :create-seed="{ bopId, moduleId, stepId }" />
    </template>
  </div>

  <notification-saved v-model="notifySaved" />
</template>

<style scoped lang="scss">
h3 {
  margin-top: 2em;
  margin-bottom: 1em;
}
.first-heading {
  margin-top: 1em;
}
.section-unused {
  opacity: 0.5;
}
</style>
